import PointIcon from "@/assets/images/point.svg";
import { BeneficiaryVisibilityType } from "@/modules/beneficiary/common/enum";
import type { IBeneficiary } from "@/modules/beneficiary/common/types";
import { DeleteBeneficiaryModal } from "@/modules/beneficiary/components/DeleteBeneficiaryModal";
import { RenameBeneficiaryModal } from "@/modules/beneficiary/components/RenameBeneficiaryModal";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { ButtonIcon } from "@ab.uitools/ui-library/components/ButtonIcon";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Image } from "@ab.uitools/ui-library/components/Image";
import { Menu } from "@ab.uitools/ui-library/components/Menu";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import type { TClickEventType } from "@ab.uitools/ui-library/types/globalTypes";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { BeneficiaryLogo } from "./BeneficiaryLogo";

interface Props extends IBeneficiary {
  index: number;
  type: string;
  isActive?: boolean;
  refetch: () => void;
  onItemClick: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const BeneficiaryItem = ({
  id,
  name,
  index,
  type,
  isActive,
  refetch,
  onItemClick,
  onDelete,
  cardEntriesCount,
  accountEntriesCount,
}: Props) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isRenameOpen, setIsRenameOpen] = useState<boolean>(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);

  const openMenu = (e: TClickEventType) => {
    e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  const menuItems = [
    {
      label: t("general.rename"),
      value: "beneficiary_rename",
      handler: () => setIsRenameOpen(true),
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Rename}/>
        ),
      },
    },
    {
      label: t("general.delete"),
      value: "beneficiary_delete",
      handler: () => {
        setRowId(id);
        setIsDeleteOpen(true);
      },
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Trash}/>
        ),
      },
    },
  ];

  return (
    <div
      className={classNames(
        "flexbox align-items--center pl-32 pr-32 widget-content-item cursor-pointer",
        {
          "beneficiary-item--active": isActive || isMenuOpen,
        }
      )}
      onClick={() => onItemClick(id)}
    >
      <div className="flexbox align-items--center justify-content--center">
        <BeneficiaryLogo name={name}/>
      </div>
      <div className="flexbox flex-direction--column pl-16 beneficiary-item-content-wrapper">
        {!!index && <Divider color="light" type="primary" isHorizontal/>}
        <div className="flexbox full-width beneficiary-item-body-wrapper">
          <div
            className="flexbox flex-direction--column beneficiary-name-column justify-content--center">
            <Text className="beneficiary-name pr-16 truncate" size="medium" weight="semibold">
              {name}
            </Text>
            <div className="flexbox align-items--center mt-4">
              <Text
                weight="bold"
                size="small"
                type="secondary"
              >
                {t("beneficiary.accountsCount", { count: accountEntriesCount })}
              </Text>
              <Image className="point-icon mx-4" imagePath={PointIcon}/>
              <Text
                weight="bold"
                size="small"
                type="secondary"
              >
                {t("beneficiary.cardsCount", { count: cardEntriesCount })}
              </Text>
            </div>
          </div>
          {type === BeneficiaryVisibilityType.Private && (
            <div ref={setMenuRef} className="flexbox align-items--center">
              <ButtonIcon
                onClick={openMenu}
                data-testid="open-beneficiary-item-action-menu"
                data-ga-id="open-beneficiary-item-action-menu"
                data-ga-action="Open beneficiary item action menu"
                size="large"
                className={classNames("item-action-button", { active: isMenuOpen })}
                iconProps={{
                  Component: MoreIcon,
                  type: "secondary",
                }}
              />
              <Menu
                position={Positions.BOTTOM_LEFT}
                onClose={() => setIsMenuOpen(false)}
                isOpen={isMenuOpen}
                parentRef={menuRef}
                additionalRef={menuRef}
                menuItems={menuItems}
              />
            </div>
          )}
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteBeneficiaryModal
          id={rowId}
          setId={setRowId}
          refetch={refetch}
          beneficiaryName={name}
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          onDelete={onDelete}
        />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <RenameBeneficiaryModal
          id={id}
          isOpen={isRenameOpen}
          beneficiaryName={name}
          onClose={() => setIsRenameOpen(false)}
          refetch={refetch}
        />
      </div>
    </div>
  );
};
